import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import {
  type CreateExternalBankAccountData,
  type IExternalBankAccountRepository,
} from '@webapp/platform/banking/business-logic/interfaces/IExternalBankAccountRepository'
import { type ExternalBankAccountConstructor } from '@webapp/platform/banking/entities/ExternalBankAccount'
import { type ExternalBankAccountApiModel } from '../../api-models/ExternalBankAccountApiModel'
import { mapExternalBankAccountApiModel } from '../../mappers/mapExternalBankAccountApiModel'
import { mapExternalBankAccountFormToPayload } from '../../mappers/mapExternalBankAccountFormToPayload'
import { type GetExternalBankAccountsResponse } from '../../types/GetExternalBankAccountsResponse'

interface Dependency {
  httpClient: IHttpClient
}

export class ExternalBankAccountRepository implements IExternalBankAccountRepository {
  constructor(private readonly deps: Dependency) {}

  async getExternalBankAccounts(
    params?: RepoFilterParams
  ): Promise<RepoPaginatedResponse<ExternalBankAccountConstructor>> {
    try {
      const res =
        await this.deps.httpClient.get<GetExternalBankAccountsResponse>(
          `/api/v2/ap/company-payment-methods/`,
          {
            params: formatRepoFiltersToApiFilters(params),
          }
        )

      const externalBankAccounts = res.data.data || []
      const metadata = res.data.metadata

      return {
        data: externalBankAccounts.map((account) =>
          mapExternalBankAccountApiModel(account)
        ),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async create(
    data: CreateExternalBankAccountData
  ): Promise<ExternalBankAccountConstructor> {
    try {
      const res = await this.deps.httpClient.post<{
        data: ExternalBankAccountApiModel
      }>(
        `/api/v2/ap/company-payment-methods/`,
        mapExternalBankAccountFormToPayload(data)
      )

      return mapExternalBankAccountApiModel(res.data.data)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  // Trailing slash is deliberate — Angular's slash-less URL triggered a 301
  // redirect hop; React calls the canonical route directly.
  async delete(id: number): Promise<void> {
    try {
      await this.deps.httpClient.delete(
        `/api/v2/ap/company-payment-methods/${id}/`
      )
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
